import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, RadioGroup } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface GateItem {
  id: string;
  item: string;
  criteria: string;
  passFail: string;
  slice: string;
  owner: string;
}

export default function CriteriaGatePage() {
  const [items, setItems] = useState<GateItem[]>([
    { id: '1', item: '', criteria: '', passFail: '', slice: '', owner: '' },
  ]);

  const updateItem = (id: string, field: keyof GateItem, value: string) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems([...items, { id: Date.now().toString(), item: '', criteria: '', passFail: '', slice: '', owner: '' }]);
  };

  return (
    <TaskPage
      phase="prioritize"
      title="Criteria Gate"
      pageNumber={1}
      instruction="If it doesn't pass, it doesn't plan. Take your Arrange ranked shortlist. For each item, run the Decide-phase rules ('we say yes only if…'). If Fail, either slice or move to Later."
      whyItMatters="Explicit criteria help novices avoid expert blind spots and reduce decision friction."
      coachRail={{
        timing: "10 minutes",
        tips: "Model one Pass and one Fail out loud; avoid rabbit holes—timebox decisions.",
        debrief: "How many passed? How many need slicing or deferring?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6 overflow-x-auto">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Gate Table</h3>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[var(--hover-tint)] border-b-2 border-[var(--rule)]">
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Item</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Key criterion(s) satisfied</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Pass / Fail</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">If fail: smallest slice</th>
                <th className="p-2 text-left font-semibold text-[var(--text-sec)]">Owner</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-[var(--rule)]">
                  <td className="p-2">
                    <TextInput
                      value={item.item}
                      onChange={(e) => updateItem(item.id, 'item', e.target.value)}
                      placeholder="Item from shortlist"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.criteria}
                      onChange={(e) => updateItem(item.id, 'criteria', e.target.value)}
                      placeholder="Which criteria does it meet?"
                      className="text-xs"
                    />
                  </td>
                  <td className="p-2">
                    <RadioGroup
                      options={[
                        { value: 'Pass', label: 'Pass' },
                        { value: 'Fail', label: 'Fail' }
                      ]}
                      value={item.passFail}
                      onChange={(value) => updateItem(item.id, 'passFail', value)}
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.slice}
                      onChange={(e) => updateItem(item.id, 'slice', e.target.value)}
                      placeholder="If fail: smallest slice"
                      className="text-xs"
                      disabled={item.passFail !== 'Fail'}
                    />
                  </td>
                  <td className="p-2">
                    <TextInput
                      value={item.owner}
                      onChange={(e) => updateItem(item.id, 'owner', e.target.value)}
                      placeholder="Owner"
                      className="text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={addItem}
            className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
          >
            + Add another item
          </button>
        </div>

        <Callout type="tip" phase="prioritize">
          <p className="text-sm">If an item fails, define the smallest slice that would pass the criteria, or move it to Later.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






